// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useState, useCallback } from 'react';

interface ProductImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackClassName?: string;
  productName?: string;
  category?: string;
  lazy?: boolean;
  title?: string;
  width?: number;
  height?: number;
  fetchPriority?: 'high' | 'low' | 'auto';
}

/** Genera una URL de fallback local para imágenes de productos */
function getLocalFallback(_productName?: string): string {
  return 'https://twidpdwinrndvvlujgij.supabase.co/storage/v1/object/public/content-images/solsazon-content-images/packs/pack-iniciacion-image.jpg';
}

const ProductImage = ({
  src,
  alt,
  className = '',
  fallbackClassName = '',
  productName,
  category: _category,
  lazy = true,
  title,
  width,
  height,
  fetchPriority = 'auto',
}: ProductImageProps) => {
  useStoreLanguage();
  const validSrc = isValidImageUrl(src) ? src : getLocalFallback(productName);
  const [imgSrc, setImgSrc] = useState(validSrc);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  const handleLoad = useCallback(() => {
    setStatus('loaded');
  }, []);

  const handleError = useCallback(() => {
    if (imgSrc === getLocalFallback()) {
      setStatus('error');
      return;
    }
    setImgSrc(getLocalFallback(productName));
    setStatus('loading');
  }, [imgSrc, productName]);

  if (status === 'error') {
    return (
      <div
        className={`${fallbackClassName || className} flex items-center justify-center bg-gray-100`}
        aria-label={tr(productName ? `Imagen no disponible: ${productName}` : 'Imagen no disponible')}
      >
        <i className="ri-image-line text-2xl text-gray-300"></i>
      </div>
    );
  }

  // Default dimensions for SEO if not provided - prevents layout shift
  const defaultWidth = 400;
  const defaultHeight = 500;
  const finalWidth = width || defaultWidth;
  const finalHeight = height || defaultHeight;

  return (
    <>
      {tr(status === 'loading' && (
        <div 
          className={`${className} bg-gray-100 animate-pulse`} 
          aria-hidden="true"
          style={{ width: finalWidth, height: finalHeight }}
        />
      ))}
      <img
        src={imgSrc}
        alt={tr(alt)}
        title={tr(title)}
        loading={lazy ? 'lazy' : 'eager'}
        fetchPriority={fetchPriority}
        width={finalWidth}
        height={finalHeight}
        onLoad={handleLoad}
        onError={handleError}
        className={`${className} ${status === 'loading' ? 'opacity-0 absolute inset-0' : 'opacity-100'} transition-opacity duration-300`}
      />
    </>
  );
};

function isValidImageUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (trimmed === '') return false;
  return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:image/');
}

export default ProductImage;
